import AccountLocationFields from "../../components/AccountLocationFields";
import ContactVerification from "../../components/ContactVerification";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldCheck, Upload, CheckCircle2 } from "lucide-react";
import { get, post } from "../../lib/api";
import { Button } from "../../components/ui/button";
import VerificationDialog from "../../components/VerificationDialog";
import VerificationStatus from "../../components/VerificationStatus";
const photoLabels = {
  front: "Front ID photo",
  back: "Back ID photo",
  selfie: "Selfie holding the ID",
};
function BackToProfile() {
  const location = useLocation();
  if (location.state?.returnTo === '/checkout') return <Button asChild variant="outline"><Link to="/checkout" state={location.state.checkoutState}>Return to checkout</Link></Button>;
  return <Button asChild variant="ghost" size="icon" aria-label="Back to profile">
    <Link to="/profile"><ArrowLeft className="h-5 w-5" aria-hidden="true" /></Link>
  </Button>;
}
function PhotoInput({ name, file, onChange, disabled }) {
  const [preview, setPreview] = useState("");
  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <label className="block rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
      <span className="mb-3 block text-sm font-semibold">
        {photoLabels[name]}
      </span>
      {preview ? (
        <img
          src={preview}
          alt={photoLabels[name] + " preview"}
          className="mb-3 h-40 w-full rounded-lg object-contain"
        />
      ) : (
        <div className="mb-3 flex h-32 items-center justify-center rounded-lg bg-white">
          <Upload className="h-7 w-7 text-slate-400" />
        </div>
      )}
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        required
        disabled={disabled}
        onChange={(event) => onChange(event, name)}
        className="w-full text-xs file:mr-2 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-2 file:font-medium"
      />
    </label>
  );
}
export default function AccountVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnLabel = location.state?.returnTo === '/checkout' ? 'Return to checkout' : 'Back to profile';
  const returnToPrevious = () => location.state?.returnTo === '/checkout' ? navigate('/checkout', { state: location.state.checkoutState }) : navigate('/profile');
  const [method, setMethod] = useState("DOCUMENT");
  const [customer, setCustomer] = useState(null),
    [idTypes, setIdTypes] = useState([]),
    [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false),
    [continued, setContinued] = useState(false),
    [saving, setSaving] = useState(false),
    [submitted, setSubmitted] = useState(false),
    [error, setError] = useState("");
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    completeAddress: "",
    idType: "",
  });
  const [accountLocation, setAccountLocation] = useState(null);
  const [photos, setPhotos] = useState({
    front: null,
    back: null,
    selfie: null,
  });
  useEffect(() => {
    Promise.all([get("/customers/me"), get("/customers/verification/settings")])
      .then(([profile, settings]) => {
        setCustomer(profile.customer);
        setMethod(settings.verificationMethod || "DOCUMENT");
        setIdTypes(settings.acceptedIdTypes || []);
        setForm({
          fullName: profile.customer.userId?.name || "",
          phone: profile.customer.userId?.phone || "",
          completeAddress: profile.customer.verification?.completeAddress || "",
          idType: "",
        });
      })
      .catch((e) =>
        setError(
          e.response?.data?.message || "Unable to load account verification.",
        ),
      )
      .finally(() => setLoading(false));
  }, []);
  const selectPhoto = (event, name) => {
    const file = event.target.files?.[0];
    if (
      file &&
      (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type) ||
        file.size > 4 * 1024 * 1024)
    ) {
      event.target.value = "";
      setPhotos((current) => ({ ...current, [name]: null }));
      setError("Choose a JPEG, PNG, or WebP photo up to 4 MB.");
      return;
    }
    setPhotos((current) => ({ ...current, [name]: file || null }));
    setError("");
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!agreed || !continued) return;
    if (Object.values(photos).some((photo) => !photo)) {
      setError("Upload all three photos.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (!accountLocation?.token) throw new Error("Enable GPS and capture your current location first.");
      const data = new FormData();
      data.append("locationToken", accountLocation.token);
      Object.entries(form).forEach(([key, value]) => data.append(key, value));
      data.append("consent", "true");
      Object.entries(photos).forEach(([key, file]) => data.append(key, file));
      await post("/customers/me/verification", data);
      setPhotos({ front: null, back: null, selfie: null });
      setSubmitted(true);
    } catch (e) {
      setError(
        e.response?.data?.message || e.message ||
          "Unable to submit verification. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };
  if (loading)
    return <p className="py-12 text-center">Loading account verification...</p>;
  if (!loading && customer && (method === "OTP" || ["MOBILE", "GOOGLE", "FACEBOOK"].includes(customer.userId?.authProvider))) return <div className="mx-auto max-w-2xl space-y-6"><header className="flex items-center gap-2"><BackToProfile /><h1 className="text-lg font-semibold">Account verification</h1></header><ContactVerification onVerified={response => { if (response.verified && location.state?.returnTo === '/checkout') navigate('/checkout', { state: location.state.checkoutState, replace: true }); }} /></div>;
  if (submitted)
    return (
      <div className="mx-auto max-w-xl rounded-2xl border bg-white p-8 text-center shadow-sm">
        <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-600" />
        <h1 className="text-2xl font-bold">Verification submitted</h1>
        <p className="my-4 text-slate-600">
          Your documents are under review. Your full name and phone number have
          been updated. We will notify you when a decision is made.
        </p>
        <Button onClick={returnToPrevious}>{returnLabel}</Button>
      </div>
    );
  if (!customer)
    return (
      <p role="alert" className="text-red-700">
        {error}
      </p>
    );
  if (customer.status === "VERIFIED" || customer.status === "UNDER_REVIEW")
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <VerificationStatus customer={customer} />
        <Button variant="outline" onClick={returnToPrevious}>
          {returnLabel}
        </Button>
      </div>
    );
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <header className="flex items-center gap-2">
          <BackToProfile />
          <h1 className="text-lg font-semibold">Account verification</h1>
        </header>
        <p className="mt-2 text-slate-500">
          Confirm your identity in a few simple steps.
        </p>
      </div>
      {!continued && (
        <VerificationDialog
          title="Let's verify your account"
          onClose={returnToPrevious}
        >
          <div className="mb-5 inline-flex rounded-2xl bg-emerald-50 p-4 text-emerald-700">
            <ShieldCheck className="h-9 w-9" />
          </div>
          <p className="text-lg leading-relaxed text-slate-700">
            As part of our KYC (Know Your Customer) process, we will collect
            necessary Docs to process verification request.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Prepare a valid ID and clear photos of its front, back, and yourself
            holding it.
          </p>
          <label className="mt-6 flex items-start gap-3 rounded-xl border p-4">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(event) => setAgreed(event.target.checked)}
              className="mt-1 h-4 w-4"
            />
            <span>I agree the Service terms and policies</span>
          </label>
          <Link
            to="/legals"
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-block text-sm text-primary underline"
          >
            Read service terms and policies
          </Link>
          <Button
            className="mt-6 w-full gap-2"
            disabled={!agreed}
            onClick={() => setContinued(true)}
          >
            Continue <ArrowRight className="h-4 w-4" />
          </Button>
        </VerificationDialog>
      )}
      {continued && <ContactVerification phoneOnly onVerified={(response) => setForm(current => ({ ...current, phone: response.phone }))} />}
      {continued && (
        <form
          onSubmit={submit}
          className="space-y-6 rounded-2xl border bg-white p-6 shadow-sm"
        >
          {error && (
            <p
              role="alert"
              className="rounded-xl bg-red-50 p-3 text-sm text-red-700"
            >
              {error}
            </p>
          )}
          <fieldset disabled={saving} className="space-y-5">
            <h2 className="text-lg font-semibold">Your identity details</h2>
            <label className="block text-sm font-medium">
              Full name
              <input
                required
                minLength={2}
                maxLength={150}
                autoComplete="name"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                className="mt-2 w-full rounded-lg border p-3"
              />
            </label>
            <label className="block text-sm font-medium">
              Phone number (update and verify changes in Account Settings)
              <input
                readOnly
                type="tel"
                required
                minLength={7}
                maxLength={25}
                autoComplete="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-2 w-full rounded-lg border p-3"
              />
            </label>
            <AccountLocationFields value={accountLocation} onChange={setAccountLocation} disabled={saving} />
            <label className="block text-sm font-medium">
              Type of ID
              <select
                required
                value={form.idType}
                onChange={(e) => setForm({ ...form, idType: e.target.value })}
                className="mt-2 w-full rounded-lg border bg-white p-3"
              >
                <option value="">Select an accepted ID</option>
                {idTypes.map((name) => (
                  <option key={name}>{name}</option>
                ))}
              </select>
            </label>
            {!idTypes.length && (
              <p className="text-sm text-amber-700">
                Verification is unavailable until an administrator configures
                accepted ID types.
              </p>
            )}
            <div>
              <h2 className="font-semibold">Upload your documents</h2>
              <p className="mt-1 text-sm text-slate-500">
                JPEG, PNG, or WebP. Up to 4 MB each. Keep all text and your
                face clearly visible.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {Object.keys(photoLabels).map((name) => (
                <PhotoInput
                  key={name}
                  name={name}
                  file={photos[name]}
                  onChange={selectPhoto}
                  disabled={saving}
                />
              ))}
            </div>
            <Button
              type="submit"
              disabled={saving || !idTypes.length || !accountLocation?.token}
              className="w-full"
            >
              {saving ? "Submitting..." : "Submit verification"}
            </Button>
          </fieldset>
        </form>
      )}
    </div>
  );
}
